import React, { useState, useEffect, useMemo } from "react";
import {
  getAllSubscriptionPayments,
  getAllCoursePurchases,
} from "../../../api";
import {
  CreditCard,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  History,
  TrendingUp,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  User as UserIcon,
  Search,
  RefreshCw,
} from "lucide-react";
import PageLoader from "../../../components/PageLoader";
import { Tooltip } from "@mui/material";
import Button from "../../../components/Button";

const AdminPaymentHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("subscription");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 12;

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError("");
      const [planData, courseData] = await Promise.all([
        getAllSubscriptionPayments(),
        getAllCoursePurchases(),
      ]);

      const plans = planData.map((p) => ({ ...p, itemType: "plan" }));
      const courses = (courseData || []).map((c) => ({
        ...c,
        itemType: "course",
      }));

      const combined = [...plans, ...courses].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );

      setHistory(combined);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch payment history",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (paise) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(paise / 100);
  };

  const filteredHistory = useMemo(() => {
    let data = history.filter(
      (p) => p.itemType === (activeTab === "subscription" ? "plan" : "course"),
    );

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter((p) => {
        const userName =
          `${p.userId?.first_name || ""} ${p.userId?.last_name || ""}`.toLowerCase();
        const userEmail = (p.userId?.email || "").toLowerCase();
        const itemName = (p.planName || p.courseTitle || "").toLowerCase();
        const orderId = (p.razorpayOrderId || "").toLowerCase();
        const paymentId = (p.razorpayPaymentId || "").toLowerCase();
        return (
          userName.includes(term) ||
          userEmail.includes(term) ||
          itemName.includes(term) ||
          orderId.includes(term) ||
          paymentId.includes(term)
        );
      });
    }

    return data;
  }, [history, activeTab, searchTerm]);

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredHistory.slice(start, start + itemsPerPage);
  }, [filteredHistory, currentPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleDownloadReceipt = (payment) => {
    const totalAmount = payment.amountPaise / 100;
    const gstRate = 0.18;
    const baseAmount = totalAmount / (1 + gstRate);
    const totalGst = totalAmount - baseAmount;
    const cgst = totalGst / 2;
    const sgst = totalGst / 2;

    const user = payment.userId;

    const formatCurrency = (amt) => {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      }).format(amt);
    };

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${payment.razorpayPaymentId || "Payment"}</title>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap" rel="stylesheet">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #1e293b; background: #f8fafc; line-height: 1.5; }
            .receipt-box { max-width: 800px; margin: auto; border: 1px solid #e2e8f0; padding: 60px; border-radius: 32px; background: white; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); position: relative; overflow: hidden; }
            .receipt-box::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 8px; background: linear-gradient(to right, #38915c, #2e744a); }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 60px; }
            .logo-container { display: flex; align-items: center; gap: 12px; }
            .logo-text { font-size: 24px; font-weight: 900; color: #0f172a; letter-spacing: -0.025em; }
            .logo-text span { color: #38915c; }
            .receipt-badge { background: #f0fdf4; color: #166534; padding: 6px 16px; border-radius: 100px; font-size: 12px; font-weight: 700; text-transform: uppercase; border: 1px solid #dcfce7; }
            .receipt-title { font-size: 40px; font-weight: 900; margin-bottom: 40px; color: #0f172a; letter-spacing: -0.05em; }
            
            .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-bottom: 60px; }
            .info-block h4 { font-size: 11px; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
            .info-block p { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
            .info-block span { font-size: 13px; color: #64748b; font-weight: 500; }

            .item-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
            .item-table th { text-align: left; border-bottom: 2px solid #f1f5f9; padding: 16px 0; font-size: 11px; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
            .item-table td { padding: 24px 0; border-bottom: 1px solid #f1f5f9; }
            
            .summary-table { width: 300px; margin-left: auto; margin-bottom: 60px; }
            .summary-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
            .summary-row.total { border-top: 2px solid #f1f5f9; margin-top: 16px; padding-top: 16px; font-size: 24px; font-weight: 900; color: #0f172a; }
            .summary-label { color: #64748b; font-weight: 600; }
            .summary-value { font-weight: 700; color: #1e293b; }

            .footer { margin-top: 80px; text-align: center; border-top: 1px solid #f1f5f9; padding-top: 40px; }
            .footer p { font-size: 14px; color: #64748b; font-weight: 500; }
            .company-info { margin-top: 20px; font-size: 12px; color: #94a3b8; font-weight: 600; line-height: 1.8; }
            
            @media print { 
              body { background: white; padding: 0; }
              .receipt-box { box-shadow: none; border: none; padding: 40px; }
              .no-print { display: none; }
            }
            .no-print-btn { position: fixed; bottom: 40px; right: 40px; background: #38915c; color: white; padding: 16px 32px; border-radius: 16px; font-weight: 800; cursor: pointer; border: none; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2); transition: all 0.2s; z-index: 100; }
            .no-print-btn:hover { transform: translateY(-2px); opacity: 0.9; }
          </style>
        </head>
        <body>
          <div class="receipt-box">
            <div class="header">
              <div class="logo-container">
                <div class="logo-text">MARKET<span>MAVERICKS</span></div>
              </div>
              <div class="receipt-badge">Paid Successfully</div>
            </div>
            
            <div class="receipt-title">Tax Invoice</div>
            
            <div class="info-section">
              <div class="info-block">
                <h4>Customer Details</h4>
                <p>${`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.name || "Valued Customer"}</p>
                <span>${user?.email || "N/A"}</span>
              </div>
              <div class="info-block">
                <h4>Invoice Details</h4>
                <p>#INV-${payment.razorpayPaymentId?.slice(-6) || Date.now().toString().slice(-6)}</p>
                <span>Date: ${formatDate(payment.createdAt)}</span><br/>
                <span>Payment ID: ${payment.razorpayPaymentId || "N/A"}</span>
              </div>
            </div>
            
            <table class="item-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style="text-align: right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <p style="font-weight: 700; color: #0f172a;">${payment.planName || payment.courseTitle}</p>
                    <p style="font-size: 12px; color: #64748b; margin-top: 4px;">${payment.itemType === "plan" ? "Subscription Plan" : "Professional Course Access"}</p>
                  </td>
                  <td style="text-align: right; font-weight: 700;">${formatCurrency(baseAmount)}</td>
                </tr>
              </tbody>
            </table>

            <div class="summary-table">
              <div class="summary-row">
                <span class="summary-label">Taxable Amount</span>
                <span class="summary-value">${formatCurrency(baseAmount)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">CGST (9%)</span>
                <span class="summary-value">${formatCurrency(cgst)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">SGST (9%)</span>
                <span class="summary-value">${formatCurrency(sgst)}</span>
              </div>
              <div class="summary-row total">
                <span class="summary-label" style="color: #0f172a;">Total</span>
                <span class="summary-value">${formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <div class="footer">
              <p>Thank you for choosing Market Mavericks!</p>
              <div class="company-info">
                MARKET MAVERICKS PVT LTD<br/>
                GSTIN: 27AAECM1234F1Z5 (Mock)<br/>
                Support: help@marketmavericks.com | Web: www.marketmavericks.com
              </div>
            </div>
          </div>
          <button class="no-print-btn no-print" onclick="window.print()">Download Invoice as PDF</button>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
  };

  return (
    <div>
      {/* Admin Header Section */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <History className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              Manage
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Payment History</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Monitor all user subscriptions and course purchases across the
            platform.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={fetchHistory}>
            Refresh
          </Button>

          {/* <button 
            onClick={fetchHistory}
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-primary-600 hover:border-primary-200 transition-all shadow-sm"
            title="Refresh Data"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button> */}
        </div>
      </div>

      {/* Search & Tabs Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex p-1 bg-gray-100 rounded-xl border border-gray-200 w-fit">
          <button
            onClick={() => handleTabChange("subscription")}
            className={`flex items-center gap-2 px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
              activeTab === "subscription"
                ? "bg-white text-primary-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <TrendingUp size={14} />
            Subscriptions
          </button>
          <button
            onClick={() => handleTabChange("course")}
            className={`flex items-center gap-2 px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
              activeTab === "course"
                ? "bg-white text-primary-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <BookOpen size={14} />
            Courses
          </button>
        </div>

        <div className="relative group min-w-[300px]">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors"
            size={16}
          />
          <input
            type="text"
            placeholder="Search user, email, order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 bg-white border border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/5 rounded-xl text-sm transition-all w-full outline-none shadow-sm"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-danger-50 border border-danger-100 text-danger-600 flex items-center gap-3 text-sm font-medium">
          <AlertCircle size={18} className="shrink-0" />
          {error}
        </div>
      )}

      {loading && history.length === 0 ? (
        <PageLoader />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/80 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    Customer
                  </th>
                  <th className="text-left py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    {activeTab === "subscription" ? "Plan" : "Course"}
                  </th>
                  <th className="text-left py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    Amount
                  </th>
                  <th className="text-left py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    Date
                  </th>
                  <th className="text-left py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    Status
                  </th>
                  <th className="text-right py-3.5 px-4 font-semibold text-gray-700 text-sm">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="text-gray-300" size={32} />
                        <p className="text-sm font-medium">
                          No payments found for your search.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentData.map((payment) => (
                    <tr
                      key={payment._id}
                      className="hover:bg-gray-50/50 transition-colors group"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center font-bold text-xs shrink-0 border border-primary-100">
                            {(payment.userId?.first_name?.[0] || "") +
                              (payment.userId?.last_name?.[0] || "") || (
                              <UserIcon size={14} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-sm truncate">
                              {`${payment.userId?.first_name || ""} ${payment.userId?.last_name || ""}`}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {payment.userId?.email || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800 text-sm">
                            {payment.planName || payment.courseTitle}
                          </span>
                          <span className="text-[11px] text-gray-500 uppercase font-bold tracking-tight">
                            {payment.itemType === "plan"
                              ? `${payment.billingCycle}`
                              : "Course"}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 text-sm">
                            {formatAmount(payment.amountPaise)}
                          </span>
                          <span className="text-[10px] text-gray-400 font-bold uppercase">
                            {payment.currency}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={13} className="text-gray-400" />
                          {formatDate(payment.createdAt)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {payment.status === "captured" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-100">
                            <CheckCircle2 size={12} />
                            SUCCESS
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-700 text-[11px] font-bold border border-red-100">
                            <XCircle size={12} />
                            FAILED
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Tooltip title="Download Receipt">
                          <button
                            onClick={() => handleDownloadReceipt(payment)}
                            disabled={payment.status !== "captured"}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <Download size={16} />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50 border-t border-gray-100">
              <p className="text-sm text-gray-500 font-medium">
                Showing{" "}
                <span className="text-gray-900 font-bold">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>
                -
                <span className="text-gray-900 font-bold">
                  {Math.min(currentPage * itemsPerPage, filteredHistory.length)}
                </span>{" "}
                of
                <span className="text-gray-900 font-bold">
                  {" "}
                  {filteredHistory.length}
                </span>{" "}
                results
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, currentPage - 2);
                    let end = Math.min(totalPages, start + maxVisible - 1);

                    if (end - start + 1 < maxVisible) {
                      start = Math.max(1, end - maxVisible + 1);
                    }

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    return pages.map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                          currentPage === pageNum
                            ? "bg-primary-600 text-white shadow-md"
                            : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPaymentHistory;
